import React, { useState } from 'react';
import { X, Upload, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { tokenStorage } from '../utils/tokenStorage';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type FeedbackType = 'BUG_REPORT' | 'FEATURE_REQUEST';

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('BUG_REPORT');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [referenceId, setReferenceId] = useState('');

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);

      if (screenshots.length + newFiles.length > 3) {
        setErrorMessage('Maximum 3 screenshots allowed');
        return;
      }

      const oversizedFile = newFiles.find(f => f.size > 5 * 1024 * 1024);
      if (oversizedFile) {
        setErrorMessage(`File "${oversizedFile.name}" exceeds the 5MB limit`);
        return;
      }

      const invalidFile = newFiles.find(f => !['image/png', 'image/jpeg'].includes(f.type));
      if (invalidFile) {
        setErrorMessage('Only PNG and JPG files are accepted');
        return;
      }

      setErrorMessage('');
      setScreenshots([...screenshots, ...newFiles]);
    }
  };

  const removeScreenshot = (index: number) => {
    setScreenshots(screenshots.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim() || !description.trim()) {
      setErrorMessage('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('feedbackType', feedbackType);
      formData.append('subject', subject.trim());
      formData.append('description', description.trim());
      formData.append('userAgent', navigator.userAgent);
      formData.append('pageUrl', window.location.href);

      const userName = localStorage.getItem('clinlexis_user_name');
      if (userName) {
        formData.append('userName', userName);
      }

      screenshots.forEach(file => {
        formData.append('screenshots', file);
      });

      const token = tokenStorage.getToken();

      const response = await fetch('/api/notifications/feedback', {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to submit feedback');
      }

      const result = await response.json();
      setReferenceId(result.referenceId || '');
      setSubmitStatus('success');

      setTimeout(() => {
        handleClose();
      }, 3000);

    } catch (error: any) {
      console.error('Feedback submission error:', error);
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFeedbackType('BUG_REPORT');
    setSubject('');
    setDescription('');
    setScreenshots([]);
    setSubmitStatus('idle');
    setErrorMessage('');
    setReferenceId('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-medical-200">
          <h2 className="text-xl font-semibold text-medical-900">Report an Issue</h2>
          <button
            onClick={handleClose}
            className="text-medical-400 hover:text-medical-600 transition-colors"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {submitStatus === 'success' ? (
            <div className="flex flex-col items-center justify-center py-8">
              <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
              <h3 className="text-lg font-medium text-medical-900 mb-2">Thank you!</h3>
              <p className="text-medical-600 text-center">
                Your feedback has been submitted successfully.
                {referenceId && <> Your reference ID is <strong>{referenceId}</strong>.</>}
                {' '}You'll receive a confirmation email shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Feedback Type */}
              <div>
                <label className="block text-sm font-medium text-medical-700 mb-2">Type</label>
                <div className="flex space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="feedbackType"
                      value="BUG_REPORT"
                      checked={feedbackType === 'BUG_REPORT'}
                      onChange={() => setFeedbackType('BUG_REPORT')}
                      className="text-primary-600 focus:ring-primary-500"
                      disabled={isSubmitting}
                    />
                    <span className="text-sm text-medical-700">Bug Report</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="feedbackType"
                      value="FEATURE_REQUEST"
                      checked={feedbackType === 'FEATURE_REQUEST'}
                      onChange={() => setFeedbackType('FEATURE_REQUEST')}
                      className="text-primary-600 focus:ring-primary-500"
                      disabled={isSubmitting}
                    />
                    <span className="text-sm text-medical-700">Feature Request</span>
                  </label>
                </div>
              </div>

              {/* Subject */}
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-medical-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="input-field"
                  placeholder="Brief description of the issue"
                  disabled={isSubmitting}
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-medical-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="input-field min-h-[120px]"
                  placeholder="Please provide detailed information about the issue, including steps to reproduce if applicable"
                  disabled={isSubmitting}
                  required
                />
              </div>

              {/* Screenshots */}
              <div>
                <label className="block text-sm font-medium text-medical-700 mb-2">
                  Screenshots (Optional)
                </label>
                <p className="text-xs text-medical-500 mb-3">
                  Upload up to 3 screenshots to help us understand the issue better
                </p>

                {screenshots.length < 3 && (
                  <label className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-medical-300 rounded-lg cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors">
                    <div className="text-center">
                      <Upload className="h-8 w-8 text-medical-400 mx-auto mb-2" />
                      <span className="text-sm text-medical-600">
                        Click to upload or drag and drop
                      </span>
                      <span className="text-xs text-medical-500 block mt-1">
                        PNG, JPG up to 5MB each
                      </span>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/png,image/jpeg,image/jpg"
                      multiple
                      onChange={handleFileChange}
                      disabled={isSubmitting}
                    />
                  </label>
                )}

                {/* Screenshot previews */}
                {screenshots.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {screenshots.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-medical-50 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-medical-200 rounded flex items-center justify-center overflow-hidden">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`Screenshot ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-medical-900">{file.name}</p>
                            <p className="text-xs text-medical-500">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeScreenshot(index)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                          disabled={isSubmitting}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Error message */}
              {errorMessage && (
                <div className="flex items-start space-x-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              )}
            </form>
          )}
        </div>

        {/* Footer */}
        {submitStatus !== 'success' && (
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-medical-200">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="btn-primary flex items-center space-x-2"
              disabled={isSubmitting || !subject.trim() || !description.trim()}
            >
              {isSubmitting ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <span>Submit Feedback</span>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
